
import { useState } from 'react';

export default function Home() {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('organization', formData.organization);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3v1qs01gb80hj212e50', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', organization: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const weeklyFocus = [
    {
      title: 'Learning Mindset',
      description: 'Participants explore how adopting a learning and growth mindset helps them face challenges with curiosity instead of fear, setting the tone for progress throughout the program.'
    },
    {
      title: 'Understanding Emotions',
      description: 'Learners identify basic emotions, recognize emotional triggers, and begin connecting feelings to actions and outcomes.'
    },
    {
      title: 'Regulating Emotions',
      description: 'Builds self-control and composure through strategies that help participants manage stress, frustration, and conflict constructively.'
    },
    {
      title: 'Honest Expression',
      description: 'Focuses on clear, respectful communication and sharing one\'s perspective without blame or defensiveness.'
    },
    {
      title: 'Taking Ownership',
      description: 'Encourages accountability for decisions and follow-through, emphasizing the connection between reliability and trust.'
    },
    {
      title: 'Learning from Mistakes',
      description: 'Teaches reflection and recovery skills, helping participants turn setbacks into opportunities for improvement.'
    },
    {
      title: 'Active Listening',
      description: 'Strengthens communication by practicing empathy, attention, and genuine understanding of others.'
    },
    {
      title: 'Giving Feedback',
      description: 'Covers how to offer and receive feedback productively, supporting teamwork and continuous improvement.'
    },
    {
      title: 'Showing Responsibility',
      description: 'Reinforces personal reliability—meeting commitments, showing up on time, and completing assigned tasks.'
    },
    {
      title: 'Building Trust',
      description: 'Focuses on honesty, consistency, and respect as foundations for relationships at home, work, and in the community.'
    },
    {
      title: 'Self-Care',
      description: 'Emphasizes mental and physical well-being, introducing daily practices that sustain focus and resilience.'
    },
    {
      title: 'Reflection & Intention',
      description: 'Participants review their growth, set forward-looking goals, and define how they will apply these skills beyond the program.'
    }
  ];

  const participantQuotes = [
    {
      quote: "It gave me a routine again — people who listened, lessons that made sense, and a safe place to practice.",
      name: "Marcus T."
    },
    {
      quote: "I learned how to communicate better with my family and potential employers. The skills are real and they work.",
      name: "Sarah M."
    },
    {
      quote: "For the first time in years, I felt like someone believed in my potential. The program gave me hope and practical tools.",
      name: "David R."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/d72009ffd1e4e89ac0953842d25105ae/a8bdca05c3d86b9715a1a035506fc54d.png"
                alt="SF Job Ready Logo"
                className="h-10 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#about" className="text-slate-600 hover:text-teal-600 transition-colors cursor-pointer">About</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-teal-600 transition-colors cursor-pointer">How It Works</a>
              <a href="#outcomes" className="text-slate-600 hover:text-teal-600 transition-colors cursor-pointer">Outcomes</a>
              <button 
                onClick={scrollToForm}
                className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Involved
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('https://static.readdy.ai/image/d72009ffd1e4e89ac0953842d25105ae/f9df14a3812b52a78e015ab1fc569802.png')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="w-full max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Building Durable Skills for Stability & Employment
            </h1>
            <p className="text-xl lg:text-2xl text-slate-200 mb-8 leading-relaxed">
              A flexible program designed for San Francisco residents to strengthen personal accountability, emotional regulation, and readiness for work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={scrollToForm}
                className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Involved
              </button>
              <button 
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-slate-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About the Program */}
      <section id="about" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-6">
                About the Program
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                SF Job Ready is a 12-week hybrid durable skills program that strengthens San Francisco Pretrial's In-Custody Referral model. It pairs trusted in-person facilitation with a lightweight digital companion to build the mindsets, skills, and confidence essential for employment, accountability, and long-term stability.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                The program helps participants develop personal agency, emotional regulation, communication, and reliability — creating measurable growth in readiness for work and community life. By combining human connection and data insight, SF Job Ready supports judges, probation officers, and community partners in reducing recidivism and improving outcomes citywide.
              </p>
              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
                <p className="text-xl font-semibold text-teal-800 italic">
                  "Human connection first. Real skills for real life."
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/d72009ffd1e4e89ac0953842d25105ae/78a5904bd4802ee9724c04a7b09d3d85.png"
                alt="Group facilitation session"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">How It Works</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Four core components working together to create lasting change
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center bg-slate-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-group-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">In-Person Facilitation</h3>
              <p className="text-slate-600">Turnkey facilitation along with staff professional development available.</p>
            </div>
            
            <div className="text-center bg-slate-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-smartphone-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Technology Platform</h3>
              <p className="text-slate-600">Mobile access and short digital lessons.  Measures attention and engagement.</p>
            </div>
            
            <div className="text-center bg-slate-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-bar-chart-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Reporting Tools</h3>
              <p className="text-slate-600">Improve outcomes and share progress with partners and funders.</p>
            </div>
            
            <div className="text-center bg-slate-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Certificate of Completion</h3>
              <p className="text-slate-600">A signal to employers of readiness and reliability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Flexible Learning Paths and Entry Points */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Flexible Learning Paths and Entry Points
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              A hopeful journey building durable skills, action orientation, and persistence.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {weeklyFocus.map((focus, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800">{focus.title}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">{focus.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Participant Outcomes */}
      <section id="outcomes" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/655307c3956aae121c5b6aefdc071f69.jpg"
                alt="Job readiness and community support"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-8">
                Participant Outcomes
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">Improved Job Readiness & Confidence</h3>
                    <p className="text-slate-600">Participants develop the durable skills, mindsets, and persistence needed for sustainable employment.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">Reduced Recidivism</h3>
                    <p className="text-slate-600">Through self-management skills and sense of belonging in the community.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">Strengthened System Collaboration</h3>
                    <p className="text-slate-600">Better coordination between justice and workforce development systems.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">Path to Stability</h3>
                    <p className="text-slate-600">A consistent pathway toward personal and community stability.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portrait of a SF Job Ready Certificate Holder */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Portrait of a SF Job Ready Certificate Holder
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Future outcomes of growth and transformation
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {participantQuotes.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm border border-slate-200">
                <div className="mb-6">
                  <i className="ri-double-quotes-l text-3xl text-teal-600"></i>
                </div>
                <p className="text-lg text-slate-700 mb-6 italic leading-relaxed">
                  {testimonial.quote}
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-user-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">Program Graduate</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Get Involved
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Interested in partnering, referring participants, or learning more about SF Job Ready? We'd love to hear from you.
            </p>
          </div>
          
          <div className="bg-slate-50 p-8 lg:p-12 rounded-xl">
            <form id="sf-job-ready-contact" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-slate-700 mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Your organization"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  placeholder="your.email@example.com"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none"
                  placeholder="Tell us about your interest in SF Job Ready..."
                />
                <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 characters</p>
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Contact SF Job Ready'}
                </button>
              </div>
              
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-center">
                  Thank you for your message! We'll get back to you soon.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-center">
                  There was an error sending your message. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/d72009ffd1e4e89ac0953842d25105ae/a8bdca05c3d86b9715a1a035506fc54d.png"
                  alt="SF Job Ready Logo"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-slate-300 leading-relaxed">
                Building durable skills for stability and employment in San Francisco.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Program</h3>
              <ul className="space-y-2 text-slate-300">
                <li><a href="#about" className="hover:text-white transition-colors cursor-pointer">About</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors cursor-pointer">How It Works</a></li>
                <li><a href="#outcomes" className="hover:text-white transition-colors cursor-pointer">Outcomes</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Get Involved</h3>
              <ul className="space-y-2 text-slate-300">
                <li>
                  <button 
                    onClick={scrollToForm}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Contact Us
                  </button>
                </li>
                <li><span className="text-slate-400">Partner with Us</span></li>
                <li><span className="text-slate-400">Refer Participants</span></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2024 SF Job Ready. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
